import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";

const ddmmyyyy = (s) => {
  if (!s) return "-";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
};

export default function StockHistory() {
  const { variantId } = useParams();
  const [sp] = useSearchParams();
  const start = sp.get("start") || "";
  const end = sp.get("end") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("physical");

  useEffect(() => {
    setLoading(true);
    api.get("/stock/history", { params: { variant_id: variantId, start: start || undefined, end: end || undefined, mode } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat riwayat"))
      .finally(() => setLoading(false));
  }, [variantId, start, end, mode]);

  const doExport = async () => {
    try { await downloadFile("/stock/history/export", { variant_id: variantId, start: start || undefined, end: end || undefined, mode }, "stock-history.xlsx"); }
    catch { toast.error("Gagal mengunduh"); }
  };

  if (loading) return <div className="p-10 text-center text-cocoa">Memuat...</div>;
  if (!data) return <div className="p-10 text-center text-status-reject">Data tidak ditemukan.</div>;

  const cols = data.columns || [];
  const alloc = data.allocation || [];
  const at = data.allocation_totals || { available: 0, keep: 0, allocated: 0, other: 0, total: 0 };
  const hasOther = (at.other || 0) > 0;

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="stock-history-page">
      <style>{`@media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; } }`}</style>

      <div className="no-print sticky top-0 bg-ivory border-b border-taupe/20 px-6 py-3 flex items-center justify-between">
        <Button variant="ghost" onClick={() => window.close()} className="text-cocoa" data-testid="sh-close-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Tutup
        </Button>
        <div className="flex gap-2 items-center">
          <div className="flex rounded-md overflow-hidden border border-taupe/40" data-testid="sh-mode-toggle">
            <button onClick={() => setMode("physical")} className={`px-3 py-1.5 text-sm ${mode === "physical" ? "bg-cocoa text-ivory" : "bg-white text-cocoa"}`} data-testid="sh-mode-physical">Stok Fisik</button>
            <button onClick={() => setMode("available")} className={`px-3 py-1.5 text-sm ${mode === "available" ? "bg-cocoa text-ivory" : "bg-white text-cocoa"}`} data-testid="sh-mode-available">Available</button>
          </div>
          <Button variant="outline" onClick={doExport} className="border-taupe text-cocoa" data-testid="sh-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export Excel
          </Button>
          <Button onClick={() => window.print()} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="sh-print-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <h1 className="text-2xl font-bold text-center">Stock History Ventedaily</h1>
        <h2 className="text-lg font-bold text-center mt-1">
          {data.product_name} {data.color ? `Warna ${data.color}` : ""} {data.size ? `Ukuran ${data.size}` : ""}
        </h2>
        <div className="mt-6 text-sm">
          <div className="font-semibold">Periode :</div>
          <div>{ddmmyyyy(start)} sd {ddmmyyyy(end)}</div>
        </div>

        <div className="mt-6" data-testid="sh-allocation">
          <div className="font-bold text-cocoa mb-2">Alokasi Stok Saat Ini <span className="font-normal text-slate-500 text-xs">(dari data master — akurat)</span></div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-100 text-left">
                  <th className="py-2 px-3 font-bold">Gudang</th>
                  <th className="py-2 px-3 font-bold text-right">Tersedia</th>
                  <th className="py-2 px-3 font-bold text-right">Keep</th>
                  <th className="py-2 px-3 font-bold text-right">Penjualan</th>
                  {hasOther && <th className="py-2 px-3 font-bold text-right">Lainnya</th>}
                  <th className="py-2 px-3 font-bold text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {alloc.map((a) => (
                  <tr key={a.col} className="border-t border-slate-200" data-testid={`sh-alloc-row-${a.col}`}>
                    <td className="py-1.5 px-3 font-medium text-cocoa">{a.warehouse}</td>
                    <td className="py-1.5 px-3 text-right">{a.available}</td>
                    <td className="py-1.5 px-3 text-right">{a.keep || "-"}</td>
                    <td className="py-1.5 px-3 text-right">{a.allocated || "-"}</td>
                    {hasOther && <td className="py-1.5 px-3 text-right">{a.other || "-"}</td>}
                    <td className="py-1.5 px-3 text-right font-semibold">{a.total}</td>
                  </tr>
                ))}
                {!alloc.length && (
                  <tr><td colSpan={hasOther ? 6 : 5} className="py-6 text-center text-slate-400">Tidak ada stok tersisa di gudang mana pun.</td></tr>
                )}
              </tbody>
              {alloc.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-cocoa">
                    <td className="py-2 px-3">Total</td>
                    <td className="py-2 px-3 text-right">{at.available}</td>
                    <td className="py-2 px-3 text-right">{at.keep}</td>
                    <td className="py-2 px-3 text-right">{at.allocated}</td>
                    {hasOther && <td className="py-2 px-3 text-right">{at.other}</td>}
                    <td className="py-2 px-3 text-right">{at.total}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className="mt-6 font-bold text-cocoa">Pergerakan Stok & PO Menunggu Penerimaan</div>
        <div className="text-xs text-slate-500 mb-2">Posisi per gudang dihitung mundur dari stok terkini, sehingga baris teratas = posisi nyata sekarang. PO pending adalah informasi pesanan dan tidak menambah stok sampai barang diterima.</div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="sh-table">
            <thead>
              <tr className="border-y-2 border-slate-300 text-left">
                <th className="py-2 px-2 font-bold">No</th>
                <th className="py-2 px-2 font-bold">User</th>
                <th className="py-2 px-2 font-bold">Date</th>
                <th className="py-2 px-2 font-bold">Reference</th>
                <th className="py-2 px-2 font-bold">Activity</th>
                <th className="py-2 px-2 font-bold">Status</th>
                <th className="py-2 px-2 font-bold">From Stock</th>
                <th className="py-2 px-2 font-bold">To Stock</th>
                <th className="py-2 px-2 font-bold text-right">Qty</th>
                <th className="py-2 px-2 font-bold text-right">All Stock</th>
                {cols.map((c) => (
                  <th key={c.id} className="py-2 px-2 font-bold text-right whitespace-nowrap">{c.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr
                  key={i}
                  className={`border-b border-slate-200 ${i % 2 ? "bg-slate-50" : ""}`}
                  data-testid={r.is_pending_purchase ? `sh-pending-po-${i}` : `sh-movement-${i}`}
                >
                  <td className="py-1.5 px-2">{i + 1}</td>
                  <td className="py-1.5 px-2">{r.user}</td>
                  <td className="py-1.5 px-2">{fmtDate(r.datetime)}</td>
                  <td className="py-1.5 px-2">
                    {r.reference_id && r.reference_type === "SALES" ? (
                      <a href={`/sales?focus=${r.reference_id}`} target="_blank" rel="noreferrer"
                         className="text-cocoa underline underline-offset-2 hover:text-cocoa-dark no-print-link"
                         data-testid={`sh-ref-link-${i}`}>{r.reference}</a>
                    ) : <div>{r.reference}</div>}
                    {r.reference_detail && <div className="text-xs text-slate-500" data-testid={`sh-ref-detail-${i}`}>{r.reference_detail}</div>}
                    {r.reference_ambiguous && <div className="text-xs text-amber-700" data-testid={`sh-ref-ambiguous-${i}`}>nomor dipakai &gt;1 transaksi</div>}
                  </td>
                  <td className="py-1.5 px-2">
                    <div>{r.activity}</div>
                    {r.is_pending_purchase && (
                      <div className="text-xs text-slate-500">
                        Pesan {r.ordered_qty} · Diterima {r.received_qty} · Sisa {r.remaining_qty}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 px-2">{r.status}</td>
                  <td className="py-1.5 px-2">{r.from_stock}</td>
                  <td className="py-1.5 px-2">{r.to_stock}</td>
                  <td className={`py-1.5 px-2 text-right ${!r.is_pending_purchase && r.qty < 0 ? "text-red-600" : !r.is_pending_purchase && r.qty > 0 ? "text-emerald-700" : ""}`}>{r.is_pending_purchase ? "-" : r.qty}</td>
                  <td className="py-1.5 px-2 text-right font-semibold">{r.total}</td>
                  {cols.map((c) => (
                    <td key={c.id} className="py-1.5 px-2 text-right">{r.balances?.[c.id] ?? 0}</td>
                  ))}
                </tr>
              ))}
              {!data.rows.length && (
                <tr><td colSpan={10 + cols.length} className="py-10 text-center text-slate-400">Tidak ada pergerakan pada periode ini.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
